import { useState } from 'react';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from '../screens/manage/common.tsx';
import { Icon } from './Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, Switch } from './ui.tsx';

/**
 * Payment terms (modules/invoicing terms.ts): an invoice paid in instalments (30% now, 70% in 30
 * days) and a discount for paying early (2% within 10 days). Set up once in the invoicing
 * settings, given to customers, and chosen on each invoice.
 */

export interface Instalment {
  percent: number;
  days: number;
}
export interface PaymentTerm {
  id: string;
  name: string;
  instalments: Instalment[];
  discount_percent: number;
  discount_days: number;
  active: boolean;
  note: string | null;
  customers?: number;
}
export interface ScheduledPayment {
  due: string;
  amount: number;
}

export const MAX_INSTALMENTS = 12;

const pct = (n: number) => String(Math.round(n * 100) / 100);

/** "30% now · 70% in 30 days · 2% off within 10 days" */
export function termSummary(term: Pick<PaymentTerm, 'instalments' | 'discount_percent' | 'discount_days'>) {
  const parts =
    term.instalments.length === 1
      ? [term.instalments[0]!.days ? tn('terms.net_days', term.instalments[0]!.days) : t('terms.on_receipt')]
      : term.instalments.map((i) => (i.days ? t('terms.part_in', { pct: pct(i.percent), days: tn('terms.days_n', i.days) }) : t('terms.part_now', { pct: pct(i.percent) })));
  if (term.discount_percent > 0) parts.push(t('terms.discount_within', { pct: pct(term.discount_percent), days: tn('terms.days_n', term.discount_days) }));
  return parts.join(' · ');
}

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const percentOf = (amount: number, p: number) => {
  const v = (amount * Math.round(p * 1000)) / 100000;
  return Math.sign(v) * Math.round(Math.abs(v));
};

/** The instalments of an invoice of `total` dated `date`, worked out as the server does. */
export function scheduleOf(term: Pick<PaymentTerm, 'instalments'>, date: string, total: number): ScheduledPayment[] {
  let left = total;
  return term.instalments.map((i, n) => {
    const amount = n === term.instalments.length - 1 ? left : percentOf(total, i.percent);
    left -= amount;
    return { due: addDays(date, i.days), amount };
  });
}

/** The payment terms on offer, for pickers. */
export function usePaymentTerms() {
  return useAction<PaymentTerm[]>('invoicing.payment_terms', { archived: false });
}

/** Chooses a payment term; '' is none (the due date is then typed or the customer's days). */
export function TermSelect({
  terms,
  value,
  onChange,
  noneLabel,
  ...aria
}: {
  terms: PaymentTerm[] | null | undefined;
  value: string;
  onChange: (id: string) => void;
  noneLabel?: string;
  /** Given by the Field around it, so its label names the select. */
  id?: string;
  'aria-describedby'?: string;
}) {
  const list = terms ?? [];
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)} {...aria}>
      <option value="">{noneLabel ?? t('terms.none')}</option>
      {list.map((term) => (
        <option key={term.id} value={term.id}>
          {term.name}
        </option>
      ))}
      {value && !list.some((x) => x.id === value) ? <option value={value}>{t('terms.archived_one')}</option> : null}
    </select>
  );
}

/** When an invoice with this term is paid: the instalments and the discount, for the editor and the invoice. */
export function SchedulePreview({ term, date, total, fmt = money }: { term: PaymentTerm; date: string; total: number; fmt?: (n: number) => string }) {
  const schedule = scheduleOf(term, date, total);
  return (
    <div className="terms-preview" aria-live="polite">
      <ul>
        {schedule.map((s, i) => (
          <li key={i}>
            <span>{day(s.due)}</span>
            <span className="muted">{pct(term.instalments[i]!.percent)}%</span>
            <strong>{fmt(s.amount)}</strong>
          </li>
        ))}
      </ul>
      {term.discount_percent > 0 ? (
        <p className="muted small">
          <Icon name="percent" size={14} /> {t('terms.discount_by', { pct: pct(term.discount_percent), date: day(addDays(date, term.discount_days)) })}
        </p>
      ) : null}
    </div>
  );
}

/** The payment terms of the business, in the invoicing settings. */
export function PaymentTermsPanel() {
  const { can } = useSession();
  const [archived, setArchived] = useState(false);
  const { data, reload } = useAction<PaymentTerm[]>('invoicing.payment_terms', { archived });
  const [open, setOpen] = useState<PaymentTerm | 'new' | null>(null);
  const shown = data?.filter((x) => archived || x.active);
  return (
    <section className="panel" aria-labelledby="terms-title">
      <div className="panel-head">
        <div>
          <h2 id="terms-title">{t('terms.title')}</h2>
          <p className="muted">{t('terms.intro')}</p>
        </div>
        {can('invoicing.settings') ? (
          <Button icon="plus" onClick={() => setOpen('new')}>
            {t('terms.add')}
          </Button>
        ) : null}
      </div>
      <div className="list-card">
        {!shown ? (
          <Skeleton h={120} />
        ) : shown.length === 0 ? (
          <EmptyState icon="calendar" title={t('terms.empty')} body={t('terms.empty_body')} />
        ) : (
          shown.map((term) => (
            <button key={term.id} className={`list-row${term.active ? '' : ' archived'}`} onClick={() => setOpen(term)} disabled={!can('invoicing.settings')}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="calendar" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{term.name}</span>
                <span className="list-sub">{[termSummary(term), term.customers ? tn('terms.customers_n', term.customers) : null].filter(Boolean).join(' · ')}</span>
              </span>
              <Icon name="chevronRight" size={18} />
            </button>
          ))
        )}
      </div>
      <div className="row-actions">
        <Button size="sm" variant="ghost" onClick={() => setArchived(!archived)}>
          {archived ? t('terms.hide_archived') : t('terms.show_archived')}
        </Button>
      </div>
      {open ? (
        <TermDialog
          term={open === 'new' ? null : open}
          onClose={() => setOpen(null)}
          onSaved={async () => {
            setOpen(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

interface DraftInstalment {
  key: string;
  percent: string;
  days: string;
}
const num = (s: string, decimals: number) => {
  const clean = s.replace(/\s/g, '').replace(',', '.');
  if (!(decimals ? new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`) : /^\d+$/).test(clean)) return null;
  return Number(clean);
};

function TermDialog({ term, onClose, onSaved }: { term: PaymentTerm | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(term?.name ?? '');
  const [rows, setRows] = useState<DraftInstalment[]>((term?.instalments ?? [{ percent: 100, days: 30 }]).map((i, n) => ({ key: `i${n}`, percent: pct(i.percent), days: String(i.days) })));
  const [discount, setDiscount] = useState(term?.discount_percent ? pct(term.discount_percent) : '');
  const [discountDays, setDiscountDays] = useState(term?.discount_percent ? String(term.discount_days) : '');
  const [active, setActive] = useState(term?.active ?? true);
  const parsed = rows.map((r) => ({ percent: num(r.percent, 2), days: num(r.days, 0) }));
  const sum = Math.round(parsed.reduce((s, r) => s + (r.percent ?? 0) * 100, 0)) / 100;
  const discountN = discount.trim() ? num(discount, 2) : 0;
  const discountDaysN = discountN ? num(discountDays, 0) : 0;
  const last = Math.max(0, ...parsed.map((r) => r.days ?? 0));
  const error =
    name.trim().length < 2
      ? t('terms.need_name')
      : parsed.some((r) => r.percent === null || r.percent <= 0 || r.days === null || r.days > 730)
        ? t('terms.row_rule')
        : sum !== 100
          ? t('terms.sum_rule', { sum: pct(sum) })
          : parsed.some((r, i) => i > 0 && r.days! <= parsed[i - 1]!.days!)
            ? t('terms.order_rule')
            : discountN === null || discountN > 50
              ? t('terms.discount_rule')
              : discountN && (discountDaysN === null || discountDaysN >= last)
                ? t('terms.discount_days_rule')
                : null;
  const set = (key: string, patch: Partial<DraftInstalment>) => setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => {
    const lastDays = rows.length ? (num(rows[rows.length - 1]!.days, 0) ?? 0) : 0;
    setRows([...rows, { key: `n${Date.now()}`, percent: sum < 100 ? pct(100 - sum) : '', days: String(lastDays + 30) }]);
  };
  const save = () =>
    void run(
      async () => {
        await action('invoicing.save_payment_term', {
          ...(term ? { id: term.id } : {}),
          name: name.trim(),
          instalments: parsed.map((r) => ({ percent: r.percent, days: r.days })),
          discount_percent: discountN || 0,
          discount_days: discountN ? discountDaysN : 0,
          active,
        });
        await onSaved();
      },
      term ? t('terms.saved', { name: name.trim() }) : t('terms.added', { name: name.trim() }),
    );
  const sample = error ? null : { id: '', name, instalments: parsed as Instalment[], discount_percent: discountN || 0, discount_days: discountDaysN || 0, active, note: null };
  return (
    <Dialog
      title={term ? term.name : t('terms.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!!error}>
            {term ? t('common.save') : t('terms.add')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('terms.name')} hint={t('terms.name_hint')}>
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <fieldset className="option-group">
          <legend className="field-label">
            {t('terms.instalments')} <span className="muted">{t('terms.instalments_hint')}</span>
          </legend>
          {rows.map((r, i) => (
            <div key={r.key} className="terms-row">
              <span className="terms-row-n muted">{i + 1}.</span>
              <span className="input-unit">
                <input className="input" inputMode="decimal" value={r.percent} aria-label={t('terms.percent_of', { n: i + 1 })} onChange={(e) => set(r.key, { percent: e.target.value })} />
                <span>%</span>
              </span>
              <span className="muted">{t('terms.due_after')}</span>
              <span className="input-unit">
                <input className="input" inputMode="numeric" value={r.days} aria-label={t('terms.days_of', { n: i + 1 })} onChange={(e) => set(r.key, { days: e.target.value })} />
                <span>{t('terms.days')}</span>
              </span>
              <IconButton icon="x" label={t('terms.remove', { n: i + 1 })} disabled={rows.length === 1} onClick={() => setRows(rows.filter((x) => x.key !== r.key))} />
            </div>
          ))}
          <div className="terms-foot">
            <Button size="sm" icon="plus" onClick={addRow} disabled={rows.length >= MAX_INSTALMENTS}>
              {t('terms.add_instalment')}
            </Button>
            <span className={sum === 100 ? 'muted' : 'warn'}>{t('terms.sum', { sum: pct(sum) })}</span>
          </div>
        </fieldset>
        <fieldset className="option-group">
          <legend className="field-label">
            {t('terms.early')} <span className="muted">{t('terms.early_hint')}</span>
          </legend>
          <div className="terms-row">
            <span className="input-unit">
              <input className="input" inputMode="decimal" placeholder="0" value={discount} aria-label={t('terms.discount')} onChange={(e) => setDiscount(e.target.value)} />
              <span>%</span>
            </span>
            <span className="muted">{t('terms.if_paid_within')}</span>
            <span className="input-unit">
              <input className="input" inputMode="numeric" placeholder="10" value={discountDays} aria-label={t('terms.discount_days')} disabled={!discountN} onChange={(e) => setDiscountDays(e.target.value)} />
              <span>{t('terms.days')}</span>
            </span>
          </div>
        </fieldset>
        {term ? (
          <div className="switch-row">
            <span>{t('terms.in_use')}</span>
            <Switch checked={active} label={t('terms.in_use')} onChange={setActive} />
          </div>
        ) : null}
        {error ? (
          <p className="callout callout-warn" role="alert">
            <Icon name="alert" size={16} /> {error}
          </p>
        ) : sample ? (
          <div>
            <p className="field-label">{t('terms.example', { amount: money(100_000_00) })}</p>
            <SchedulePreview term={sample} date={new Date().toISOString().slice(0, 10)} total={100_000_00} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
